import { useMemo, useState } from 'react';
import { isEmpty, pickBy } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { firmIntelligenceUrl } from 'common/config/urls';
import { FIRM_FUND_STEPPER_BASE_STATE, FIRM_FUND_STEPPER_TITLES } from 'common/constants/firms';
import { Firm } from 'common/types/firm';
import { FirmForm } from 'components/Forms';
import { FirmFormState } from 'components/Forms/FirmForm/FirmForm.props';
import Stepper from 'components/Stepper';
import { useFundForm } from 'pages/Funds/forms/FundDialogForm/hooks';
import { FirmHk } from 'services/hooks';
import { CreateFirmResult, FirmStepperProps, StepperState } from './FirmStepper.props';

const FirmStepper = ({ isVisible, onClose }: FirmStepperProps) => {
  const [stepperState, setStepperState] = useState<StepperState>({
    firm: FIRM_FUND_STEPPER_BASE_STATE as FirmFormState,
    fund: FIRM_FUND_STEPPER_BASE_STATE as FirmFormState,
  });

  const { firm } = stepperState;

  const navigate = useNavigate();

  const { useCreateNewFirm } = FirmHk;
  const [, createFirm] = useCreateNewFirm();

  const { formState: fundFormState, FundForm } = useFundForm({ fund: undefined });

  const isStepperEmpty = useMemo(
    () => isEmpty(pickBy(firm.values)) && isEmpty(pickBy(fundFormState.values)),
    [firm, fundFormState]
  );

  const handleFormStateChange = (step: string, formState: FirmFormState) => {
    setStepperState(previousState => ({
      ...previousState,
      [step]: {
        ...previousState[step as keyof StepperState],
        ...formState,
      },
    }));
  };

  const onSubmit = async () => {
    const { values: firmData } = firm;
    const { values: fundData } = fundFormState;

    const allData = {
      ...firmData,
      fund: fundData,
    } as unknown as Firm;

    // Create Firm
    const data = (await createFirm(allData)) as unknown as CreateFirmResult;
    const { response } = data;

    if (response?.status) {
      const errors = response.body;

      if (errors) {
        const { fund: fundErrors, ...firmErrors } = errors;

        setStepperState({
          ...stepperState,
          firm: {
            ...stepperState.firm,
            dbErrors: firmErrors,
          },
          fund: {
            ...stepperState.firm,
            dbErrors: fundErrors ?? null,
          },
        });

        return false;
      }
    }

    navigate(firmIntelligenceUrl(data.slug as string));
    return true;
  };

  const steps = [
    {
      id: 'firm',
      title: 'Firm',
      isValid: firm.isValid,
      component: (
        <FirmForm
          defaultFormState={firm as unknown as FirmFormState}
          onFormChange={state => handleFormStateChange('firm', state)}
        />
      ),
    },
    {
      id: 'fund',
      title: 'Fund',
      isValid: fundFormState.isValid,
      component: FundForm,
    },
  ];

  return (
    <Stepper
      id="firm"
      isVisible={isVisible}
      title="Create New Firm"
      stepperTitles={FIRM_FUND_STEPPER_TITLES}
      steps={steps}
      onClose={onClose}
      onSubmit={onSubmit}
      confirmCancel={!isStepperEmpty}
    />
  );
};

export default FirmStepper;
